import { practiceFilterMappings } from '../../pages/resource-matrix/practices/practiceFilterMappings';
import { PracticesResponse } from '../../types';
import { analytics } from '../../utils/analytics';
import { getCustomerIdFromToken } from '../../utils/getCustomerIdFromToken';
import { get } from '../apiService';

type PracticesListResponse = {
  data: PracticesResponse | null;
  loading: boolean;
};

const practicesListInFlight = new Map<string, Promise<PracticesListResponse>>();

// Drop any pending requests whose practiceIds filter overlaps `ids` so that
// the next caller gets a genuinely fresh network response rather than
// reusing a pre-invalidation in-flight.
export const cancelPracticesListRequestsFor = (ids: string[]): void => {
  for (const key of [...practicesListInFlight.keys()]) {
    try {
      const parsed = JSON.parse(key) as {
        filters?: { practiceIds?: string[] };
      };
      const keyIds = parsed?.filters?.practiceIds ?? [];
      if (ids.some((id) => keyIds.includes(id))) {
        practicesListInFlight.delete(key);
      }
    } catch {
      // skip malformed keys
    }
  }
};

const buildPracticesListRequestKey = (
  startToken?: string | null,
  filters?: Record<string, string[] | string | undefined>,
  limit?: number,
): string => {
  const normalizedFilters: Record<string, string | string[]> = {};

  if (filters) {
    Object.keys(filters)
      .sort()
      .forEach((key) => {
        const value = filters[key];
        if (value === undefined || value === '') return;

        normalizedFilters[key] = Array.isArray(value)
          ? [...value].filter(Boolean).sort()
          : value;
      });
  }

  return JSON.stringify({
    startToken: startToken ?? null,
    limit: limit ?? null,
    filters: normalizedFilters,
  });
};

export const getPracticesList = async (
  startToken?: string | null,
  filters?: Record<string, string[] | string | undefined>,
  limit?: number,
): Promise<PracticesListResponse> => {
  const requestKey = buildPracticesListRequestKey(
    startToken,
    filters,
    limit,
  );
  const existing = practicesListInFlight.get(requestKey);
  if (existing) {
    return existing;
  }

  const request = (async (): Promise<PracticesListResponse> => {
  const customer = getCustomerIdFromToken();
  if (!customer) {
    throw new Error('Customer Id is not available');
  }

  // Base query parameters
  const queryParams: Record<string, string> = {
    customerId: customer,
  };

  if (startToken) {
    queryParams.startToken = startToken;
  }

  if (limit) {
    queryParams.limit = limit.toString();
  }

  // Append only non-empty filters with proper mapping
  if (filters) {
    Object.entries(filters).forEach(([uiKey, value]) => {
      if (value !== undefined && uiKey !== 'undefined') {
        const apiKey = practiceFilterMappings[uiKey] ?? uiKey;
        if (apiKey) {
          // Special handling for marketIds, states, and practiceIds - backend expects multiple parameters when arrays
          if (
            (apiKey === 'marketIds' ||
              apiKey === 'states' ||
              apiKey === 'practiceIds') &&
            Array.isArray(value)
          ) {
            // Don't add to queryParams here, we'll handle it separately
            return;
          }

          // For other filters or single values, use single value (ensure it's a string)
          queryParams[apiKey] = Array.isArray(value)
            ? value[0]
            : value;
        } else {
          console.warn(`Unmapped filter key detected: ${uiKey}`);
        }
      }
    });
  }

  // Build URL with query parameters
  const urlSearchParams = new URLSearchParams(queryParams);

  // Add multiple marketIds parameters if present (array)
  // Single marketIds values are already in queryParams above
  if (filters?.marketIds && Array.isArray(filters.marketIds)) {
    filters.marketIds.forEach((marketId) => {
      if (marketId) {
        urlSearchParams.append('marketIds', marketId);
      }
    });
  }

  // Add multiple states parameters if present (array)
  // Single states values are already in queryParams above
  if (filters?.states && Array.isArray(filters.states)) {
    filters.states.forEach((state) => {
      if (state) {
        urlSearchParams.append('states', state);
      }
    });
  }

  // Add multiple practiceIds parameters if present (array)
  if (filters?.practiceIds && Array.isArray(filters.practiceIds)) {
    filters.practiceIds.forEach((pid) => {
      if (pid) urlSearchParams.append('practiceIds', pid);
    });
  }

  let url = `resourceMatrix/practices/list?${urlSearchParams.toString()}`;

  if (startToken) {
    url += `&startToken=${startToken}`;
  }

  let loading = true;
  try {
    const responseData = await get(url);
    const data = responseData as PracticesResponse;
    loading = false;
    analytics.trackEvent('list', 'practice');
    return { data, loading };
  } catch (error) {
    loading = false;
    console.error('❌ Failed to fetch practices list:', error);
    throw new Error('Failed to fetch practices list');
  }
  })();

  practicesListInFlight.set(requestKey, request);

  try {
    return await request;
  } finally {
    const current = practicesListInFlight.get(requestKey);
    if (current === request) {
      practicesListInFlight.delete(requestKey);
    }
  }
};
